// Shared HTML-tag stripping for member-message previews.
//
// PadSplit-delivered messages can arrive as raw HTML markup (e.g. `<img …>`).
// The full markup is preserved in `member_chats.message_text` because the
// in-thread view renders it safely via DOMPurify, but the short inbox / search
// / notification previews derived from that text should be plain text — never
// raw `<img …>` markup. This helper is the single source of truth for turning a
// message body into a clean preview string, used server-side when the preview
// is computed (see `server/storage.ts`) so every consumer of the preview field
// gets clean text. (Task #3189)

// Matches an HTML <img> tag anywhere in the string.
export const HTML_IMG_REGEX = /<img\b[^>]*>/i;

// Strips all HTML tags from a string.
export const STRIP_TAGS_REGEX = /<[^>]+>/g;

// Strips HTML tags from a message body for use as a preview. When the source
// contained an `<img>` tag and nothing else (i.e. an image-only message),
// returns the label "Image" so the preview isn't rendered as an empty string.
export function stripHtmlForPreview(text: string | null | undefined): string {
  if (!text) return "";
  if (!STRIP_TAGS_REGEX.test(text)) return text;
  // `STRIP_TAGS_REGEX` carries the global flag, so reset its `lastIndex` before
  // the replace below (the `.test()` above advances it).
  STRIP_TAGS_REGEX.lastIndex = 0;
  const hadImg = HTML_IMG_REGEX.test(text);
  const textOnly = text.replace(STRIP_TAGS_REGEX, "").trim();
  if (textOnly.length === 0) return hadImg ? "Image" : "";
  return textOnly;
}
